import React from "react";
import { Button } from "@/components/ui/button";
import { MessageCircle } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/auth";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { getOrCreateConversation } from "@/services/chat";

interface TalkToMentorButtonProps {
  mentorId: string;
  mentorName?: string;
  hasPurchasedMentorship?: boolean; // Nova prop para indicar se já comprou mentoria
}

export const TalkToMentorButton: React.FC<TalkToMentorButtonProps> = ({
  mentorId,
  mentorName,
  hasPurchasedMentorship = false
}) => {
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const [isCreatingConversation, setIsCreatingConversation] = React.useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  
  const handleButtonClick = async () => {
    if (!user) {
      toast({
        title: "Faça login para acessar",
        description: "Você precisa estar logado para conversar com mentores.",
        variant: "destructive"
      });
      navigate("/login");
      return;
    }
    
    // Se já comprou mentoria, redirecionar diretamente para o chat
    if (hasPurchasedMentorship) {
      await createConversationAndRedirect();
      return;
    }
    
    // Verificar se tem mentorias compradas com este mentor
    try {
      const { data: sessions, error } = await supabase
        .from("sessions")
        .select("id")
        .eq("player_profile_id", user.id)
        .eq("mentor_id", mentorId)
        .in("status", ["confirmado", "concluido"])
        .limit(1);

      if (error) {
        console.error("Erro ao verificar mentorias:", error);
      }

      if (sessions && sessions.length > 0) {
        // Tem mentoria comprada, redirecionar para chat
        await createConversationAndRedirect();
      } else {
        // Não tem mentoria comprada, mostrar modal informativo
        setIsModalOpen(true);
      }
    } catch (error) {
      console.error("Erro ao verificar mentorias:", error);
      setIsModalOpen(true);
    }
  };

  const createConversationAndRedirect = async () => {
    try {
      setIsCreatingConversation(true);
      
      console.log(`🚀 [TalkToMentorButton] Criando/buscando conversa com mentor ${mentorId}`);
      
      // Criar ou obter conversa existente
      const conversationId = await getOrCreateConversation(mentorId);
      
      console.log(`✅ [TalkToMentorButton] Conversa criada/encontrada: ${conversationId}`);
      
      // Redirecionar para a página de chat do painel do jogador com a conversa específica
      navigate(`/painel-jogador/chat/${conversationId}`);
      
    } catch (error) {
      console.error("❌ [TalkToMentorButton] Erro ao criar conversa:", error);
      toast({
        title: "Erro ao abrir chat",
        description: "Não foi possível abrir o chat. Tente novamente.",
        variant: "destructive"
      });
    } finally {
      setIsCreatingConversation(false);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  return (
    <>
      <Button
        onClick={handleButtonClick}
        disabled={isCreatingConversation}
        className="w-full bg-[#00B6F0] hover:bg-[#0099cc] text-white font-medium py-2 px-4 rounded-lg transition-colors duration-200 flex items-center justify-center gap-2"
      >
        <MessageCircle className="h-4 w-4" />
        {isCreatingConversation ? "Abrindo chat..." : "Falar com Mentor"}
      </Button>

      {/* Modal informativo quando não tem mentoria comprada */}
      <Dialog open={isModalOpen} onOpenChange={(open) => !open && setIsModalOpen(false)} modal>
        <DialogContent 
          className="sm:max-w-md"
          onPointerDownOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
          onInteractOutside={(e) => e.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle className="text-center">Chat com Mentor</DialogTitle>
            <DialogDescription className="text-center text-lg py-4">
              Chat com mentor liberado após compra da mentoria
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex justify-center pt-4">
            <Button
              onClick={handleCloseModal}
              className="bg-[#00B6F0] hover:bg-[#0099cc] text-white"
            >
              Entendi
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
